import { IAppContext } from 'App/AppContext';
import IdListDTO from 'models/dto/IdListDTO';
import { RiskContextDTO } from 'models/dto/riskContextDTO';
import RiskDTO from 'models/dto/riskDTO';
import RiskStateDTO from 'models/dto/riskStateDTO';
import RiskState_TranslationDTO from 'models/dto/riskState_TranslationDTO';
import Risk_TranslationDTO from 'models/dto/risk_TranslationDTO';
import RisksDTO, { RiskDashboardDTO } from 'models/dto/risksDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import Risk from 'models/risk';
import { RiskContext } from 'models/riskContext';
import RiskState from 'models/riskState';
import RiskState_Translation from 'models/riskState_Translation';
import Risk_Translation from 'models/risk_Translation';
import Risks, { RiskDashboard, RiskDashboardPeriod } from 'models/risks';
import { fromApiDateTimeOptional } from 'utils/datetime';
import { mapFromControlDashboard, mapFromControls } from './controlMappings';
import { mapFromLanguage, mapToLanguage } from './langMapping';
import { mapFromResourceLinks } from './linkMapping';
import { mapFromObjectives } from './objectiveMapping';
import { mapFromProcesses } from './processMapping';
import { mapFromProcessingActivities } from './processingActivityMapping';
import { mapFromThemes } from './themeMapping';

export function mapFromRisksArray(risksDto: RiskDTO[] | undefined, appContext: IAppContext): Risk[] {
  if (!risksDto) return [];

  const output = risksDto.map((_riskDTO) => mapFromRisk(_riskDTO, appContext));

  return output;
}

export function mapFromRisks(risksDto: RisksDTO, appContext: IAppContext): Risks {
  const output = new Risks();
  output.risks = risksDto.risks ? risksDto.risks.map((_riskDTO) => mapFromRisk(_riskDTO, appContext)) : [];

  return output;
}

export function mapFromRisk(risk: RiskDTO, appContext: IAppContext): Risk {
  const output = new Risk();
  output.code = risk.code;
  output.externalId = risk.externalId;
  output.riskId = risk.riskId;
  output.authSchemaId = risk.authSchemaId;
  output.riskStateId = risk.riskStateId;
  output.commentTrailId = risk.commentTrailId;
  output.auditTrailId = risk.auditTrailId;
  output.groupId = risk.groupId;
  output.group = risk.groupId ? appContext.globalDataCache.groups.get(risk.groupId) : undefined;
  output.ownerId = risk.ownerId;
  output.ownerRoleId = risk.ownerRoleId;
  output.owner = risk.ownerId ? appContext.globalDataCache.users.get(risk.ownerId) : undefined;
  output.sortOrder = risk.sortOrder;
  output.likelihoodStart = risk.chanceStart;
  output.likelihoodCurrent = risk.chanceCurrent;
  output.likelihoodGoal = risk.chanceGoal;
  output.impactStart = risk.impactStart;
  output.impactCurrent = risk.impactCurrent;
  output.impactGoal = risk.impactGoal;
  output.dim3Start = risk.dim3Start;
  output.dim3Current = risk.dim3Current;
  output.dim3Goal = risk.dim3Goal;
  output.analysisTaskid = risk.analysisTaskid;
  output.method = risk.method;
  output.created = fromApiDateTimeOptional(risk.created);
  output.latestChangeDateTime = fromApiDateTimeOptional(risk.latestChangeDateTime);
  output.tagIds = risk.tagIds?.idList?.map<number>((i) => Number(i));
  output.classificationGroupIds = risk.classificationGroupIds?.idList?.map<number>((i) => Number(i)) ?? [];
  output.assetIds = risk.assetIds?.idList?.map<number>((i) => Number(i)) ?? [];
  output.trans = risk.trans
    ? risk.trans.map((_risk_TranslationDTO) => mapFromRisk_Translation(_risk_TranslationDTO))
    : [];

  output.controls = risk.controls ? mapFromControls(risk.controls, appContext.globalDataCache) : undefined;
  output.resourceLinks = risk.resourceLinks ? mapFromResourceLinks(risk.resourceLinks, appContext.globalDataCache) : [];
  output.normIds = risk.normIds?.idList?.map<number>((i) => Number(i)) ?? [];

  if (output.trans && output.trans.length > 0) {
    output.transIdx = 0;
    output.name = output.trans[0].name;
    output.asset = output.trans[0].asset;
    output.description = output.trans[0].description;
    output.analysis = output.trans[0].analysis;
    output.strategy = output.trans[0].strategy;
  }

  output.setRiskMatrix(appContext);

  return output;
}

export function mapFromRisk_Translation(risk_Translation: Risk_TranslationDTO): Risk_Translation {
  const output = new Risk_Translation();
  output.riskId = risk_Translation.riskId;
  output.languageId = risk_Translation.languageId;
  output.name = risk_Translation.name;
  output.asset = risk_Translation.asset || '';
  output.description = risk_Translation.description || '';
  output.analysis = risk_Translation.analysis || '';
  output.strategy = risk_Translation.strategy || '';
  output.lang = mapFromLanguage(risk_Translation.lang);

  return output;
}

export function mapFromRiskStates(riskStateDTO: RiskStateDTO[]): RiskState[] {
  return riskStateDTO.map((_stateDTO) => {
    return mapFromRiskState(_stateDTO);
  });
}

export function mapFromRiskState(riskStateDto: RiskStateDTO): RiskState {
  const output = new RiskState();
  output.tenantId = riskStateDto.tenantId;
  output.riskStateId = riskStateDto.riskStateId;
  output.completed = riskStateDto.completed;
  output.sortOrder = riskStateDto.sortOrder;
  output.riskCount = riskStateDto.riskCount;
  output.trans = riskStateDto.trans.map((_riskState_TranslationDTO) =>
    mapFromRiskState_Translation(_riskState_TranslationDTO),
  );

  if (output.trans && output.trans.length > 0) {
    output.state = output.trans[0].state;
  }

  return output;
}

export function mapFromRiskState_Translation(riskState_Translation: RiskState_TranslationDTO): RiskState_Translation {
  const output = new RiskState_Translation();
  output.state = riskState_Translation.state;
  output.lang = mapFromLanguage(riskState_Translation.lang);

  return output;
}

//mapToRisk methods
export function mapToRisks(risks: Risk[]): RiskDTO[] {
  return risks.map((risk) => mapToRisk(risk));
}

export function mapToRisk(risk: Risk): RiskDTO {
  const output = new RiskDTO();
  output.riskId = risk.riskId;
  output.authSchemaId = risk.authSchemaId;
  output.code = risk.code;
  output.externalId = risk.externalId;
  output.riskStateId = risk.riskStateId;
  output.code = risk.code;
  output.commentTrailId = risk.commentTrailId;
  output.auditTrailId = risk.auditTrailId;
  output.groupId = risk.groupId;
  output.sortOrder = risk.sortOrder;
  output.chanceStart = risk.likelihoodStart;
  output.chanceCurrent = risk.likelihoodCurrent;
  output.chanceGoal = risk.likelihoodGoal;
  output.impactStart = risk.impactStart;
  output.impactCurrent = risk.impactCurrent;
  output.impactGoal = risk.impactGoal;
  output.dim3Start = risk.dim3Start;
  output.dim3Current = risk.dim3Current;
  output.dim3Goal = risk.dim3Goal;
  output.analysisTaskid = risk.analysisTaskid;
  output.ownerId = risk.ownerId;
  output.ownerRoleId = risk.ownerRoleId;
  output.method = risk.method;
  output.classificationGroupIds = new IdListDTO(risk.classificationGroupIds.map((s) => s.toString()));
  output.assetIds = new IdListDTO(risk.assetIds.map((s) => s.toString()));
  output.trans = risk.trans.map((_risk_Translation) => mapToRisk_Translation(_risk_Translation));

  //controls and resourcelinks cannot be send to the API at this moment

  if (risk.trans && risk.transIdx >= 0) {
    output.trans[risk.transIdx].name = risk.name;
    output.trans[risk.transIdx].description = risk.description;
    output.trans[risk.transIdx].asset = risk.asset;
    output.trans[risk.transIdx].analysis = risk.analysis;
    output.trans[risk.transIdx].strategy = risk.strategy;
  }

  return output;
}

export function mapToRisk_Translation(risk_Translation: Risk_Translation): Risk_TranslationDTO {
  const output = new Risk_TranslationDTO();
  output.riskId = risk_Translation.riskId;
  output.languageId = risk_Translation.languageId;
  output.name = risk_Translation.name;
  output.asset = risk_Translation.asset;
  output.description = risk_Translation.description;
  output.analysis = risk_Translation.analysis;
  output.strategy = risk_Translation.strategy;
  output.lang = mapToLanguage(risk_Translation.lang);

  return output;
}

//risk context
export const mapFromRiskContext = (context: RiskContextDTO, globalCache: GlobalDataCache): RiskContext => {
  const output = new RiskContext();
  output.themes = mapFromThemes(context.themes, globalCache);
  output.processes = mapFromProcesses(context.processes, globalCache);
  output.objectives = mapFromObjectives(context.objectives, globalCache);
  output.processingActivities = mapFromProcessingActivities(context.processingActivities, globalCache);

  return output;
};

//risk dashboard
export function mapFromRiskDashboard(riskDashboardDTO: RiskDashboardDTO, cache: GlobalDataCache): RiskDashboard {
  const riskDashboard = new RiskDashboard();
  riskDashboard.controls = mapFromControls(riskDashboardDTO.controls, cache);
  riskDashboard.controlDashboards =
    riskDashboardDTO.controlDashboards?.map((controlDashboardDTO) => {
      return mapFromControlDashboard(controlDashboardDTO);
    }) ?? [];
  riskDashboard.aggregatedDashboard = riskDashboardDTO.aggregatedDashboard
    ? mapFromControlDashboard(riskDashboardDTO.aggregatedDashboard)
    : undefined;

  if (riskDashboardDTO.previousPeriod) {
    riskDashboard.previousPeriod = new RiskDashboardPeriod();
    riskDashboard.previousPeriod.startDate = fromApiDateTimeOptional(riskDashboardDTO.previousPeriod.startDate);
    riskDashboard.previousPeriod.endDate = fromApiDateTimeOptional(riskDashboardDTO.previousPeriod.endDate);
    riskDashboard.previousPeriod.controlDashboards =
      riskDashboardDTO.previousPeriod.controlDashboards?.map((controlDashboardDTO) => {
        return mapFromControlDashboard(controlDashboardDTO);
      }) ?? [];
    riskDashboard.previousPeriod.aggregatedDashboard = riskDashboardDTO.previousPeriod.aggregatedDashboard
      ? mapFromControlDashboard(riskDashboardDTO.previousPeriod.aggregatedDashboard)
      : undefined;
  } else {
    riskDashboard.previousPeriod = undefined;
  }

  return riskDashboard;
}
